import { createAssetToClusters, priceActionClusterId } from 'components/market/asset/groups/GroupService.ts';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import gYupResolver from 'components/technical/form/gYupResolver.ts';
import { useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError.tsx';
import { config as allocationConstraintConfig } from '../allocationConstraints/AllocationConstraintsStepConfig.tsx';
import { getGroupConstraints } from '../asset/AssetOptimizerRequestFactory.ts';
import {
  returnMeasureValues,
  RiskDistributionOption,
  riskDistributionValues,
} from '../assumptionsAndOutlook/AssumptionsAndOutlook.validation.tsx';
import { config as assumptionsAndOutlookConfig } from '../assumptionsAndOutlook/AssumptionsAndOutlookStepConfig.tsx';
import { config as descriptionStepConfig } from '../description/DescriptionStepConfig.tsx';
import { config as objectivesStepConfig } from '../objective/ObjectivesStepConfig.tsx';
import { config as portfolioLevelConstraintsConfig } from '../portfolioConstraints/PortfolioConstraintsStepConfig.tsx';
import { config as submitConfig } from '../submit/SubmitStepConfig.tsx';
import type {
  PortfolioDefinitionWithAssetUniverseInputFields,
  PortfolioDefinitionWithAssetUniverseOutputFields,
} from './PortfolioDefinitionWithAssetUniverse.validation.ts';

import { ContentPasteOutlined } from '@mui/icons-material';
import { Chip, Stack } from '@mui/joy';
import GFormProvider from 'components/technical/form/GFormProvider.tsx';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar.tsx';
import { GStepper } from 'components/technical/wizard/GStepper.tsx';
import type { StepConfig } from 'components/technical/wizard/StepConfig.ts';
import { useNavigate } from 'react-router';
import {
  type IAssetOptimizerWizardInputQuery,
  IConstraintFormulation,
  type ICreateRebalancingRuleInput,
  ILeverageType,
  IObjectiveType,
  type IPortfolioDefinitionOptimizerInputQuery,
  IPortfolioDefinitionSubType,
  type IRebalancedPortfolioFilterInput,
  IReturnMeasureNameUi,
  IRiskBudgetAllocation,
  IUserPortfolioDefinitionType,
  PortfolioDefDashboardInputDocument,
  useCreatePortfolioDefinitionMutation,
  useUpdatePortfolioDefinitionMutation,
} from '../../../../../generated/graphql.tsx';
import type { NotVerifiedAssetWithId } from '../../../../market/asset/AssetService.tsx';
import { useSteps } from '../../../../technical/wizard/UseSteps.ts';
import { createAssetColumns } from '../asset/AssetOptimizerWizard.tsx';
import { secondaryConstraintQuantityValues } from '../portfolioConstraints/PortfolioSecondaryConstraint.validation.ts';
import { getConstraintValue, getObjectives, getPortfolioConstraints } from '../requestFactory.ts';
import type {
  PortfolioDefinitionWithAssetScreenerInputFields,
  PortfolioDefinitionWithAssetScreenerOutputFields,
} from './PortfolioDefinitionWithAssetScreener.validation.ts';
import type * as yup from 'yup';
import isNil from 'lodash/fp/isNil';

const PortfolioDefinitionOptimizerContainer = <
  INPUT extends PortfolioDefinitionWithAssetUniverseInputFields | PortfolioDefinitionWithAssetScreenerInputFields,
  OUTPUT extends PortfolioDefinitionWithAssetUniverseOutputFields | PortfolioDefinitionWithAssetScreenerOutputFields,
>({
  universeStep,
  schema,
  createOptimizerRuleFilter,
  defaultValues,
  assets,
  multifactor,
  id,
  rebalancingRules,
}: {
  id?: string;
  universeStep: (props: {
    assets: NotVerifiedAssetWithId[];
    assetIdToClusterToGroup: Record<string, Record<string, string>>;
    goToNextStep: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: type is invariant
  }) => StepConfig<any>;
  // biome-ignore lint/suspicious/noExplicitAny: type is invariant
  schema: yup.Schema<any>;
  createOptimizerRuleFilter: (portfolio: OUTPUT) => {
    assetIds: string[];
    filters: IRebalancedPortfolioFilterInput[];
  };
  defaultValues: Partial<INPUT> | null | undefined;
  assets: IPortfolioDefinitionOptimizerInputQuery['assets'];
  multifactor: IPortfolioDefinitionOptimizerInputQuery['multifactor'];
  rebalancingRules: ICreateRebalancingRuleInput[];
}): ReactElement => {
  const assetGroups = [...assets.assetGroups.genieGroups, ...assets.assetGroups.userGroups];
  const filteredAssetGroups = assetGroups.filter((group) => group.clusterName !== priceActionClusterId);
  const assetIdToClusterToGroup = createAssetToClusters(filteredAssetGroups);

  const universeStepConfig = useCallback(
    (props: { nextHandler: () => void }): StepConfig<
      PortfolioDefinitionWithAssetScreenerInputFields | PortfolioDefinitionWithAssetUniverseInputFields
    > =>
      universeStep({
        assets: assets.feature,
        assetIdToClusterToGroup,
        goToNextStep: props.nextHandler,
      }),
    [assets, assetIdToClusterToGroup, universeStep]
  );

  return (
    <PortfolioDefinitionOptimizerWizard<INPUT, OUTPUT>
      id={id}
      multifactors={multifactor.getAllMultifactorsForUser}
      benchmarks={assets.benchmark}
      availableAssets={assets.feature}
      assetGroups={assetGroups}
      assetIdToClusterToGroup={assetIdToClusterToGroup}
      universeStep={universeStepConfig}
      schema={schema}
      defaultValues={defaultValues}
      createOptimizerRuleFilter={createOptimizerRuleFilter}
      rebalancingRules={rebalancingRules}
    />
  );
};

export type AssetGroup = {
  clusterName: string;
  groupName: string;
  assets: Array<{ id: string }>;
  id: string;
};

interface PortfolioDefinitionOptimizerWizardProps<
  INPUT extends PortfolioDefinitionWithAssetUniverseInputFields | PortfolioDefinitionWithAssetScreenerInputFields,
  OUTPUT extends PortfolioDefinitionWithAssetUniverseOutputFields | PortfolioDefinitionWithAssetScreenerOutputFields,
> {
  id?: string;
  multifactors: IAssetOptimizerWizardInputQuery['multifactor']['getAllMultifactorsForUser'];
  benchmarks: NotVerifiedAssetWithId[];
  availableAssets: NotVerifiedAssetWithId[];
  assetGroups: AssetGroup[];
  assetIdToClusterToGroup: Record<string, Record<string, string>>;
  // biome-ignore lint/suspicious/noExplicitAny: type is invariant
  universeStep: (props: { nextHandler: () => void }) => StepConfig<any>;
  // biome-ignore lint/suspicious/noExplicitAny: type is invariant
  schema: yup.Schema<any>;
  createOptimizerRuleFilter: (portfolio: OUTPUT) => {
    assetIds: string[];
    filters: IRebalancedPortfolioFilterInput[];
  };
  defaultValues: Partial<INPUT> | null | undefined;
  rebalancingRules: ICreateRebalancingRuleInput[];
}

const PortfolioDefinitionOptimizerWizard = <
  INPUT extends PortfolioDefinitionWithAssetUniverseInputFields | PortfolioDefinitionWithAssetScreenerInputFields,
  OUTPUT extends PortfolioDefinitionWithAssetUniverseOutputFields | PortfolioDefinitionWithAssetScreenerOutputFields,
>({
  id,
  multifactors,
  benchmarks,
  availableAssets,
  assetGroups,
  assetIdToClusterToGroup,
  universeStep,
  schema,
  createOptimizerRuleFilter,
  defaultValues,
  rebalancingRules,
}: PortfolioDefinitionOptimizerWizardProps<INPUT, OUTPUT>): ReactElement => {
  const methods = useForm<
    PortfolioDefinitionWithAssetUniverseInputFields | PortfolioDefinitionWithAssetScreenerInputFields
  >({
    resolver: gYupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      allowShortAndLeverage: true,
      objectives: [
        {
          type: IObjectiveType.MaxRiskAdjustedReturns,
          riskMetric: null,
        },
      ],
      constraints: [],
      universe: [],
      filters: [],
      constraintType: IConstraintFormulation.Percentage,
      returnsForecast: IReturnMeasureNameUi.HistoricMedian,
      portfolioConstraints: {
        primaryConstraint: null,
        secondaryConstraint: null,
      },
      multifactor: {
        useAbsoluteScores: false,
      },
      ...defaultValues,
    },
  });

  const stepApi = useSteps();
  const { goToStep, validateVisitedSteps } = stepApi;

  const name = useWatch({
    name: 'name',
    control: methods.control,
  });

  const steps = useMemo(() => {
    let nextIndex = 1;
    const nextHandler = (): (() => void) => {
      const index = nextIndex++;
      return (): void => goToStep(index);
    };

    const idToAsset = Object.fromEntries(availableAssets.map((asset) => [asset.id, asset]));

    const assumptionsColumns = createAssetColumns({ idToAsset });

    return [
      descriptionStepConfig({
        namePlaceholder: 'Portfolio 1',
        descriptionPlaceholder: 'Portfolio description',
        goToNextStep: nextHandler(),
      }),
      objectivesStepConfig({
        benchmarks,
        hideEquity: true,
        goToNextStep: nextHandler(),
      }),
      portfolioLevelConstraintsConfig({
        benchmarks,
        secondaryConstraintQuantityValues,
        goToNextStep: nextHandler(),
      }),
      universeStep({
        nextHandler: nextHandler(),
      }),
      allocationConstraintConfig({
        assetGroups,
        assetIdToClusterToGroup,
        allowAnyConstraints: true,
        hideExpressConstraintsAs: true,
        goToNextStep: nextHandler(),
        type: 'asset',
        allItems: availableAssets,
      }),
      assumptionsAndOutlookConfig({
        returnsForecast: {},
        riskBudgetForecast: {},
        multifactorValues: multifactors.map((fact) => ({
          value: {
            id: fact.multifactor.id,
            maxFactors: fact.maxFactors,
          },
          label: fact.multifactor.name,
          key: fact.multifactor.id.toString(),
        })),
        columns: assumptionsColumns,
        goToNextStep: nextHandler(),
        hideTable: true,
        riskDistributionValues: riskDistributionValues.filter(
          (opt) =>
            opt.availableFor.includes('portfolioDefinition') &&
            (opt.value !== RiskDistributionOption.MultifactorScore || multifactors.length > 0)
        ),
        returnMeasureValues: returnMeasureValues.filter((opt) => opt.availableFor.includes('portfolioDefinition')),
        showYield: true,
        sourceLabels: {
          universe: 'Asset universe',
          givenPortfolio: 'Initial portfolio',
        },
      }),
      submitConfig({
        label: id ? 'Update portfolio' : 'Create portfolio',
      }),
    ];
  }, [benchmarks, availableAssets, assetGroups, goToStep, multifactors, assetIdToClusterToGroup, universeStep, id]);

  validateVisitedSteps({
    trigger: methods.trigger,
    steps,
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);
  const [createPortfolioDefinition] = useCreatePortfolioDefinitionMutation({
    refetchQueries: [PortfolioDefDashboardInputDocument],
  });
  const [updatePortfolioDefinition] = useUpdatePortfolioDefinitionMutation({
    refetchQueries: [PortfolioDefDashboardInputDocument],
  });
  const navigate = useNavigate();

  const handleFormSubmit = async (
    data: PortfolioDefinitionWithAssetUniverseInputFields | PortfolioDefinitionWithAssetScreenerInputFields
  ): Promise<void> => {
    try {
      const castedPortfolio = data as unknown as OUTPUT;
      const optimizerRuleFilter = createOptimizerRuleFilter(castedPortfolio);

      const optimizerRule = {
        ...optimizerRuleFilter,
        optimizationInput: {
          assets: castedPortfolio.constraints
            .filter((constraint) => constraint.item.type === 'asset')
            .map((constraint) => ({
              id: constraint.item.asset?.id ?? '',
              constraint: getConstraintValue(
                { constraintValue: constraint.constraintValue, constraintType: constraint.constraintType },
                IConstraintFormulation.Percentage
              ),
            })),
          constraintFormulation: IConstraintFormulation.Percentage,
          returnMeasureName: castedPortfolio.returnsForecast,
          leverageType: castedPortfolio.allowShortAndLeverage ? ILeverageType.LongShort : ILeverageType.LongOnly,
          portfolioConstraints: getPortfolioConstraints(castedPortfolio),
          groupConstraints: getGroupConstraints(castedPortfolio),
          objectives: getObjectives(castedPortfolio),
          multifactor: isNil(castedPortfolio.multifactor.factor) ? null : castedPortfolio.multifactor,
          riskBudgetAllocation:
            castedPortfolio.riskBudgetAllocation === RiskDistributionOption.MultifactorScore
              ? IRiskBudgetAllocation.Multifactor
              : IRiskBudgetAllocation.AssetExpectedReturns,
        },
      };

      if (id) {
        await updatePortfolioDefinition({
          variables: {
            input: {
              id,
              name: data.name,
              description: data.description,
              optimizerRule: optimizerRule,
            },
          },
        });
      } else {
        await createPortfolioDefinition({
          variables: {
            input: {
              name: data.name,
              description: data.description,
              type: IUserPortfolioDefinitionType.Rebalanced,
              optimizerRule: optimizerRule,
              subType: IPortfolioDefinitionSubType.OptimizerBased,
              rebalancingRules,
            },
          },
        });
      }

      navigate('/app/copilot/lab/portfolio');
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  return (
    <Stack direction="column" spacing={1}>
      <HeaderBar
        title={
          <>
            {name ? name : 'Portfolio'} <Chip startDecorator={<ContentPasteOutlined fontSize="small" />}>Draft</Chip>
          </>
        }
      ></HeaderBar>
      <GFormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
          <GStepper
            goToStep={stepApi.goToStep}
            getStepState={stepApi.getStepState}
            formState={methods.formState}
            steps={steps}
            activeStep={stepApi.activeStep}
          />
        </form>
      </GFormProvider>
    </Stack>
  );
};

export default PortfolioDefinitionOptimizerContainer;
